import { X } from "@phosphor-icons/react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";

import { CAMPAIGNS_FILTERS } from "../../../constants";
import CampaignFilterOptions from "../CampaignFilterOptions";

import type { CampaignsSearchParams } from "../../../types";

function getFilterValueLabel(
  key: keyof CampaignsSearchParams,
  selectedFilters: string[],
  accounts: { id: string; full_name: string }[],
): string {
  if (selectedFilters.length > 1) {
    return `${selectedFilters.length} values`;
  }

  // For accounts filter, look up account name
  if (key === "accounts") {
    const account = accounts.find(({ id }) => id === selectedFilters[0]);
    return account?.full_name ?? "Unknown";
  }

  // For other filters, use the options from CAMPAIGNS_FILTERS
  const { options } = CAMPAIGNS_FILTERS[key];
  return (
    options[selectedFilters[0] as keyof typeof options] ?? selectedFilters[0]
  );
}

export default function DesktopFilterTags({
  searchParams,
  setSearchParams,
}: {
  searchParams: CampaignsSearchParams;
  setSearchParams: (searchParams: CampaignsSearchParams) => void;
}) {
  const { accounts } = useSelectedWorkspaceContext();

  return (
    <div className="gap-1.5 flex">
      {typedObjectEntries(searchParams).map(
        <SearchParamsKey extends keyof CampaignsSearchParams>([
          key,
          selectedFilters,
        ]: [SearchParamsKey, CampaignsSearchParams[SearchParamsKey]]) => {
          if (selectedFilters.length) {
            const { icon, label } = CAMPAIGNS_FILTERS[key];

            return (
              <div
                className="flex border border-black-200 rounded-md h-6"
                key={key}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex px-2 items-center gap-x-1 text-button-xs hover:bg-black-100 [&>svg]:size-4 [&>svg]:-ml-1">
                    {icon}
                    <span>{label}</span>
                    <span className="text-black-500">
                      {selectedFilters.length > 1 ? "is one of" : "is"}
                    </span>
                    <span>
                      {getFilterValueLabel(key, selectedFilters, accounts)}
                    </span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="min-w-[var(--radix-popper-anchor-width)] max-w-52">
                    <DropdownMenuList>
                      <CampaignFilterOptions
                        searchParamsKey={key}
                        searchParams={searchParams}
                        setSearchParams={setSearchParams}
                      />
                    </DropdownMenuList>
                  </DropdownMenuContent>
                </DropdownMenu>

                <button
                  type="button"
                  className="px-1 border-l border-l-black-200 hover:bg-black-100"
                  onClick={() =>
                    setSearchParams({ ...searchParams, [key]: [] })
                  }
                >
                  <X />
                </button>
              </div>
            );
          }
        },
      )}
    </div>
  );
}
